/**
 * V2 Share primitive — every content surface (yoink, place, profile,
 * later category/list/map) gets this button: one tap hands the page's
 * clean canonical URL to the native share sheet, so "show off your
 * progress" is always a URL away (decisions log, 2026-06-12).
 *
 * Labeled "Share link" since 2026-07-15: pages with photos also carry
 * "Share photos" (share-photos.js, the card-set/post intent), and the
 * pair of plain labels is what tells the two apart.
 *
 * navigator.share is the normal path on iPad/phone; the clipboard
 * fallback covers desktop, with the button itself confirming in plain
 * words ("Link copied").
 */
import { icon } from './icons.js';
import { markComplete } from '../features/community/tutorial.js';

/** Render the Share button (event delegation: handle via wireShare). */
export function shareButton(title) {
  return `
    <button type="button" class="v2-btn-share" data-share-title="${title.replace(/"/g, '&quot;')}">
      ${icon('share', 16)}<span data-share-label>Share link</span>
    </button>`;
}

// The query params that mean something when a link is shared — filters
// and the grid/map state (user rule 2026-07-08: shared pages open the
// way the sharer was looking at them). lat/lng/zoom are the map's
// viewport, written by map-view while a map is up and cleared when it
// goes away, so they only ride along when the map is the thing shared.
const SHARED_PARAMS = ['category', 'view', 'q', 'region', 'lat', 'lng', 'zoom'];

/**
 * The clean canonical URL for the current page — no cache busters, no
 * hash, but MEANINGFUL state survives: ?category= filters, ?q= search,
 * ?view=map, and the map viewport (?lat/?lng/?zoom).
 */
export function canonicalUrl() {
  const current = new URLSearchParams(window.location.search);
  const kept = new URLSearchParams();
  for (const name of SHARED_PARAMS) {
    const value = current.get(name);
    if (value) kept.set(name, value);
  }
  const q = kept.toString();
  return window.location.origin + window.location.pathname + (q ? `?${q}` : '');
}

/**
 * Reflect a piece of view state into the URL (replaceState — no history
 * spam), preserving the other params. Empty value removes the param.
 */
export function setUrlParam(name, value) {
  setUrlParams({ [name]: value });
}

/** Reflect several view-state params in one history write. */
export function setUrlParams(entries) {
  const params = new URLSearchParams(window.location.search);
  for (const [name, value] of Object.entries(entries)) {
    if (value) params.set(name, value);
    else params.delete(name);
  }
  const q = params.toString();
  window.history.replaceState(window.history.state, '', window.location.pathname + (q ? `?${q}` : ''));
}

/** Read a view-state param off the current URL. */
export function getUrlParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}

/**
 * Handle a click that may be on a Share button.
 * @returns {boolean} true if the click was a share action
 */
export function handleShareClick(event) {
  const button = event.target.closest('.v2-btn-share');
  if (!button) return false;
  share(button);
  return true;
}

async function share(button) {
  markComplete('share');
  if (window.location.pathname.includes('/user/')) markComplete('share_profile');
  await shareUrl(button.dataset.shareTitle || document.title, button);
}

/**
 * Share the page's canonical URL directly — for flows that intercept the
 * Share button first (a private list offering to go unlisted) and then
 * continue the share themselves. `button` is optional: with one, the
 * clipboard fallback confirms on its label; without, it stays silent.
 */
export async function shareUrl(title, button = null) {
  const url = canonicalUrl();
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
    } catch {
      // User dismissed the sheet — nothing to do
    }
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    if (button) confirmOnButton(button, 'Link copied');
  } catch {
    if (button) confirmOnButton(button, 'Couldn’t copy');
  }
}

/**
 * Share a message plus a URL through the native sheet — the
 * leaderboard's Challenge button (2026-07-19). `url` defaults to the
 * current page's canonical URL; pass it explicitly to point elsewhere
 * (the profile's category-filtered challenge links to the CATEGORY
 * page, where the leaderboard lives — not the profile it was shared
 * from). The URL is EMBEDDED in the text, on its own line, not passed
 * as a separate url field: iOS share targets that keep the text drop
 * the url field (the share-card caption learned this first — user
 * report 2026-07-19, a Wine challenge arrived linkless).
 * Messages/WhatsApp auto-link and unfurl a URL inside text, so nothing
 * is lost where both survive.
 */
export async function shareTextFrom(button, { title, text, url }) {
  const message = `${text}\n${url || canonicalUrl()}`;
  if (navigator.share) {
    try {
      await navigator.share({ title, text: message });
    } catch {
      // User dismissed the sheet — nothing to do
    }
    return;
  }
  try {
    await navigator.clipboard.writeText(message);
    confirmOnButton(button, 'Copied — send it to a friend');
  } catch {
    confirmOnButton(button, 'Couldn’t copy');
  }
}

function confirmOnButton(button, text) {
  const label = button.querySelector('[data-share-label]');
  if (!label) return;
  const original = label.textContent;
  label.textContent = text;
  setTimeout(() => {
    if (button.isConnected) label.textContent = original;
  }, 2000);
}
